import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { User, Lock, Mail, Save, Eye, EyeOff, AlertCircle, CheckCircle, Smartphone, Key } from 'lucide-react';
import { API_BASE } from '../../config';
import styles from './Settings.module.scss';
import logger from '../../utils/logger';
import { getAuthToken } from '../../utils/authStorage';

const Settings = () => {
  const { user, userInfo, refreshUserInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // OTP password reset states
  const [otpMode, setOtpMode] = useState(false);
  const [otpData, setOtpData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [adminPhone, setAdminPhone] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Initialize form with current user data
  useEffect(() => {
    if (userInfo) {
      setFormData(prev => ({
        ...prev,
        firstName: userInfo.first_name || '',
        lastName: userInfo.last_name || '',
        email: userInfo.email || user?.email || ''
      }));
      
      // Set OTP email to current user's email
      setOtpData(prev => ({
        ...prev,
        email: userInfo.email || user?.email || ''
      }));
    }
  }, [userInfo, user]);

  // Load admin phone number
  useEffect(() => {
    const loadAdminPhone = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/otp/admin-phone`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setAdminPhone(data.admin_phone);
        }
      } catch (error) {
        logger.warn('Failed to load admin phone:', error);
      }
    };
    loadAdminPhone();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear message when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleOtpInputChange = (e) => {
    const { name, value } = e.target;
    setOtpData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear message when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = (type) => {
    if (type === 'profile') {
      if (!formData.firstName.trim()) {
        setMessage({ type: 'error', text: 'First name is required' });
        return false;
      }
      if (!formData.lastName.trim()) {
        setMessage({ type: 'error', text: 'Last name is required' });
        return false;
      }
      if (!formData.email.trim()) {
        setMessage({ type: 'error', text: 'Email is required' });
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        setMessage({ type: 'error', text: 'Please enter a valid email address' });
        return false;
      }
    } else if (type === 'password') {
      if (!formData.currentPassword) {
        setMessage({ type: 'error', text: 'Current password is required' });
        return false;
      }
      if (!formData.newPassword) {
        setMessage({ type: 'error', text: 'New password is required' });
        return false;
      }
      if (formData.newPassword.length < 6) {
        setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
        return false;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setMessage({ type: 'error', text: 'New passwords do not match' });
        return false;
      }
    }
    return true;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm('profile')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        if (refreshUserInfo) {
          await refreshUserInfo();
        }
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!validateForm('password')) return;

    setLoading(true);
    try {
      // Import Firebase auth
      const { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
      
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        setMessage({ type: 'error', text: 'No user logged in' });
        return;
      }

      // Re-authenticate user with current password
      // Use the email from the form or fall back to the user's email
      const emailToUse = formData.email || user.email;
      logger.debug('Re-authenticating with email:', emailToUse);
      
      const credential = EmailAuthProvider.credential(emailToUse, formData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, formData.newPassword);
      
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      // Log the password change in the backend
      try {
        await fetch(`${API_BASE}/api/user/password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({
            current_password: formData.currentPassword,
            new_password: formData.newPassword
          })
        });
      } catch (logError) {
        logger.warn('Failed to log password change:', logError);
      }
      
    } catch (error) {
      logger.error('Password change error:', error);
      let errorMessage = 'Failed to change password. ';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage += 'Current password is incorrect.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage += 'Current password is incorrect or email does not match.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage += 'New password is too weak.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage += 'Please log out and log back in, then try again.';
      } else if (error.code === 'auth/user-mismatch') {
        errorMessage += 'Email does not match the logged-in user.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage += 'User account not found.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!otpData.email) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API_BASE}/api/otp/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: otpData.email,
          purpose: "password_reset"
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
        setMessage({ 
          type: 'success', 
          text: `OTP sent to admin phone ${adminPhone || data.admin_phone || ''}` 
        });
      } else {
        // Always show OTP input field on error since OTP might have been sent
        setOtpSent(true);
        setMessage({ 
          type: 'warning', 
          text: 'OTP may have been sent. Please check your phone and enter the code.' 
        });
      }
    } catch (error) {
      // Always show OTP input field on network error since OTP might have been sent
      logger.error('Network error requesting OTP:', error);
      setOtpSent(true);
      setMessage({ 
        type: 'warning', 
        text: 'Network error occurred, but OTP may have been sent. Please check your phone and enter the code if received.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpData.otp) {
      setMessage({ type: 'error', text: 'Please enter the OTP' });
      return;
    }

    if (!otpData.newPassword || !otpData.confirmPassword) {
      setMessage({ type: 'error', text: 'Please enter new password and confirmation' });
      return;
    }
    
    if (otpData.newPassword !== otpData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    
    if (otpData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    try {
      // Directly call password reset endpoint which handles both verification and reset
      const response = await fetch(`${API_BASE}/api/otp/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: otpData.email,
          otp: otpData.otp,
          new_password: otpData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOtpVerified(true);
        setMessage({ 
          type: 'success', 
          text: 'Password reset successful! Please log out and log back in with your new password.' 
        });
        
        // Reset OTP form
        setOtpData({
          email: formData.email,
          otp: '',
          newPassword: '',
          confirmPassword: ''
        });
        setOtpSent(false);
        setOtpVerified(false);
        setOtpMode(false);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to reset password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.settingsContainer}>
      <div className={styles.settingsHeader}>
        <h2>Account Settings</h2>
        <p>Manage your account information and security settings</p>
      </div>

      {message && message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {message.text}
        </div>
      )}

      <div className={styles.settingsContent}>
        {/* Profile Information Section */}
        <div className={styles.settingsSection}>
          <div className={styles.sectionHeader}>
            <User size={20} />
            <h3>Profile Information</h3>
          </div>
          <form onSubmit={handleProfileUpdate} className={styles.settingsForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address</label>
              <div className={styles.inputWithIcon}>
                <Mail size={16} className={styles.inputIcon} />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>
            <button 
              type="submit" 
              className={styles.saveButton}
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>

        {/* Password Change Section */}
        <div className={styles.settingsSection}>
          <div className={styles.sectionHeader}>
            <Lock size={20} />
            <h3>Change Password</h3>
          </div>
          <form onSubmit={handlePasswordChange} className={styles.settingsForm}>
            <div className={styles.formGroup}>
              <label htmlFor="currentPassword">Current Password</label>
              <div className={styles.inputWithIcon}>
                <Lock size={16} className={styles.inputIcon} />
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Enter your current password"
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="newPassword">New Password</label>
              <div className={styles.inputWithIcon}>
                <Lock size={16} className={styles.inputIcon} />
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter your new password"
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className={styles.inputWithIcon}>
                <Lock size={16} className={styles.inputIcon} />
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your new password"
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button 
              type="submit" 
              className={styles.saveButton}
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* OTP Password Reset Section */}
        <div className={styles.settingsSection}>
          <div className={styles.sectionHeader}>
            <Smartphone size={20} />
            <h3>Reset Password with OTP</h3>
          </div>
          
          {!otpMode ? (
            <div className={styles.otpModeToggle}>
              <p>Forgot your current password? Reset it using OTP sent to admin phone.</p>
              <button 
                className={styles.otpToggleButton}
                onClick={() => setOtpMode(true)}
              >
                <Key size={16} />
                Reset Password with OTP
              </button>
            </div>
          ) : (
            <form className={styles.settingsForm}>
              <div className={styles.formGroup}>
                <label htmlFor="otpEmail">Email Address</label>
                <div className={styles.inputWithIcon}>
                  <Mail size={16} className={styles.inputIcon} />
                  <input
                    type="email"
                    id="otpEmail"
                    name="email"
                    value={otpData.email}
                    onChange={handleOtpInputChange}
                    placeholder="Enter your email address"
                    required
                    disabled={otpSent}
                  />
                </div>
              </div>

              {!otpSent ? (
                <button 
                  type="button" 
                  className={styles.otpButton}
                  onClick={handleRequestOtp}
                  disabled={loading}
                >
                  <Smartphone size={16} />
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              ) : (
                <>
                  <div className={styles.formGroup}>
                    <label htmlFor="otpCode">OTP Code</label>
                    <div className={styles.inputWithIcon}>
                      <Key size={16} className={styles.inputIcon} />
                      <input
                        type="text"
                        id="otpCode"
                        name="otp"
                        value={otpData.otp}
                        onChange={handleOtpInputChange}
                        placeholder="Enter 6-digit OTP"
                        maxLength="6"
                        required
                        disabled={otpVerified}
                      />
                    </div>
                  </div>

                  {otpSent && (
                    <>
                      <div className={styles.formGroup}>
                        <label htmlFor="otpNewPassword">New Password</label>
                        <div className={styles.inputWithIcon}>
                          <Lock size={16} className={styles.inputIcon} />
                          <input
                            type="password"
                            id="otpNewPassword"
                            name="newPassword"
                            value={otpData.newPassword}
                            onChange={handleOtpInputChange}
                            placeholder="Enter new password"
                            required
                          />
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="otpConfirmPassword">Confirm New Password</label>
                        <div className={styles.inputWithIcon}>
                          <Lock size={16} className={styles.inputIcon} />
                          <input
                            type="password"
                            id="otpConfirmPassword"
                            name="confirmPassword"
                            value={otpData.confirmPassword}
                            onChange={handleOtpInputChange}
                            placeholder="Confirm new password"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {otpSent && !otpVerified && (
                    <div className={styles.otpActions}>
                      <button 
                        type="button" 
                        className={styles.otpButton}
                        onClick={handleVerifyOtp}
                        disabled={loading}
                      >
                        <CheckCircle size={16} />
                        {loading ? 'Resetting Password...' : 'Reset Password with OTP'}
                      </button>
                      
                      <button 
                        type="button" 
                        className={styles.cancelButton}
                        onClick={() => {
                          setOtpMode(false);
                          setOtpSent(false);
                          setOtpVerified(false);
                          setOtpData({
                            email: formData.email,
                            otp: '',
                            newPassword: '',
                            confirmPassword: ''
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {otpVerified && (
                    <div className={styles.successMsg}>
                      <CheckCircle size={16} />
                      Password reset successful! Please log out and log back in with your new password.
                    </div>
                  )}
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
